<template>
    <div class="mt-5 w-full max-w-7xl">
        <!-- Wallet header with arrows and dropdown (same UX as SubAdminToPerson) -->
        <div class="w-full mt-5">
            <div class="w-full flex items-center gap-3">
                <!-- Left arrow -->
                <button type="button"
                    :class="['w-[40px] h-[124px] bg-white border border-content-basic-4 rounded-02 flex items-center justify-center shrink-0', canPrev ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed']"
                    :disabled="!canPrev"
                    @click="prevWallet">
                    <ChevronLeftIcon :width="36" :height="36" color="text-content-basic-2" weight="REGULAR" />
                </button>

                <!-- Wallet bar -->
                <div class="relative flex-1 w-[936px] h-[124px] bg-primary-3 rounded-02 flex items-center justify-center px-6">
                    <div class="flex flex-col items-center">
                        <div class="relative">
                            <button type="button"
                                class="text-white font-inter-heading-14 flex items-center gap-2" 
                                @click="toggleWalletDropdown">
                                    {{ selectedWallet.walletCategoryName || 'Select Wallet' }}
                                <span class="inline-flex items-center justify-center w-[24px] h-[24px] rounded-full bg-[#364EBE]">
                                    <ArrowDropDownIcon
                                        :width="16"
                                        :height="16"
                                        color="text-white"
                                        weight="REGULAR"
                                        :class="[showWalletDropdown ? 'rotate-180' : '', 'transition-transform', 'block']" />
                                </span>
                            </button>
                            <div v-if="showWalletDropdown" class="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-20 w-72">
                                <div class="absolute left-0 top-0 w-full max-h-60 overflow-y-auto bg-background-basic-1 border border-border-basic-2 rounded-01_5 shadow ds-scrollbar">
                                    <button
                                        v-for="w in wallets"
                                        :key="w.walletCategorySeq"
                                        type="button"
                                        class="w-full text-left"
                                        @click="selectWallet(w.walletCategorySeq)"
                                    >
                                        <div class="px-3 py-3 hover:bg-background-basic-2">
                                            <span class="font-inter-heading-20 text-content-basic-1">{{ w.walletCategoryName }}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div class="text-white font-inter-heading-1 mt-2">{{ numberWithCommas(selectedWallet.subAdminAvailableMileage) }} Points</div>
                    </div>
                </div>

                <!-- Right arrow -->
                <button type="button"
                    :class="['w-[40px] h-[124px] bg-white border border-content-basic-4 rounded-02 flex items-center justify-center shrink-0', canNext ? 'hover:bg-gray-50' : 'opacity-50 cursor-not-allowed']"
                    :disabled="!canNext"
                    @click="nextWallet">
                    <ChevronRightIcon :width="36" :height="36" color="text-content-basic-2" weight="REGULAR" />
                </button>
            </div>
        </div>

        <!-- File upload section -->
        <div class="w-full mt-5 bg-white border border-content-basic-4 rounded-02">
            <div class="w-full p-10">
                <div class="w-full h-[1px] bg-[#D9D9D9] relative">
                    <div class="w-[160px] bg-[#fff] absolute text-center left-[50%] translate-x-[-50%] top-[-10px]">File Upload</div>
                </div>
            </div>
            <div class="flex justify-between items-center gap-5 flex-wrap p-5">
                <div class="flex justify-between items-center gap-5 grow">
                    <a :href="require(`@/assets/file/transferSampleFile.xlsx`)" :download="isTransfer ? 'transferTemplateFile.xlsx' : 'deductTemplateFile.xlsx'">
                        <ElementsButton text="Download Template File" :fitContent="true" :height12="true" />
                    </a>
                    <form @submit="clickUploadButton" ref="fileUploadForm" class="flex grow">
                        <ElementsInput v-model="uploadFile" class="grow" inputName="multipartFile" inputtype="file" placeholder="Select File" :full="true" :acceptFileFormat="['.xlsx']" />
                        <ElementsButton class="ml-5" text="Upload" :fitContent="true" :height12="true" inputtype="submit" />
                    </form>
                    <ElementsButton text="Remove" :fitContent="true" :height12="true" :backgroundRed="true" @click-event="removeFile" />
                </div>
                <p v-if="inValidFileType" class="mt-1 font-caption-md-regular text-accent-red-3">
                    {{ inValidFileTypeMessage }}
                </p>
            </div>

            <!-- Preview list -->
            <div class="w-full p-10 overflow-auto">
                <div class="min-w-full w-fit">
                    <div class="w-full h-[1px] bg-[#D9D9D9] relative">
                        <div class="w-[147px] bg-[#fff] absolute text-center left-[50%] translate-x-[-50%] top-[-10px]">
                            <span v-if="isTransfer">Transfer</span>
                            <span v-if="isDeduct">Deduct</span> List
                        </div>
                    </div>
                    <div class="w-full">
                        <div class="text-[14px] text-center mt-[80px]" v-if="excelList.length == 0">+ Add employees to deduct points</div>
                    </div>
                    <div class="mt-[80px]" v-if="excelList.length != 0">
                        <template v-for="(employee, index) in excelList" v-bind:key="index">
                            <div class="flex justify-between items-center py-2.5 w-full" :class="{ 'bg-slate-50': index % 2 !== 0 }">
                                <div class="w-20 text-sm font-normal text-center truncate">
                                    {{ index + 1 }}
                                </div>
                                <div class="text-base font-bold w-40 px-4 truncate">
                                    {{ employee.employeeName }}
                                </div>
                                <div class="w-72 px-4 text-sm font-normal text-center truncate">
                                    {{ $validateUtil.emptyValueToDash(employee.departmentName) }} / {{ $validateUtil.emptyValueToDash(employee.jobTitleName) }}
                                </div>
                                <div class="w-60 px-2 text-sm font-normal text-left"
                                     :class="employee.isNotInteger || employee.emptyAmount ? 'mt-5':''">
                                    <ElementsInput v-model="employee.mileageVolumeFormat" placeholder="0" :full="true" @input="onInputMileage($event, employee)"/>
                                    <p v-if="employee.isNotInteger" class="text-sm text-red-600 text-nowrap mt-1/2 ml-1/2">
                                        Please use whole numbers only.
                                    </p>
                                    <p v-if="employee.emptyAmount" class="text-sm text-red-600 text-nowrap mt-1/2 ml-1/2">
                                        Please enter the amount.
                                    </p>
                                </div>
                                <div class="w-60 px-2 text-sm font-normal text-center truncate">
                                    <ElementsInput v-model="employee.transactionNote" placeholder="Remark" :maxlength="20" :full="true" />
                                </div>
                                <button @click="clickDeleteButton(index)" class="w-32 px-4 border rounded border-indigo-400 text-indigo-400 py-1.5 text-center">
                                    Delete
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <!-- Message and totals -->
            <div class="w-full p-10">
                <div class="w-full h-[1px] bg-[#D9D9D9] relative">
                    <div class="w-[147px] bg-[#fff] absolute text-center left-[50%] translate-x-[-50%] top-[-10px]">
                        Message
                    </div>
                </div>
                <textarea
                    v-model="transactionMessage"
                    placeholder="Message"
                    maxlength="150"
                    @input="onMessageInput($event.target.value)"
                    :class="['mt-[50px] p-10 outline-0 w-full h-[114px] text-sm border rounded-lg bg-white transition-all duration-500 shadow shadow-gray-100', showTransactionMessageError ? 'border-accent-red-3 border' : 'border']" />
                <p v-if="showTransactionMessageError" class="mt-2 font-inter-caption-2 text-accent-red-3">{{ transactionMessageErrorMessage }}</p>
            </div>

            <div class="w-full p-10">
                <div class="w-full h-[1px] bg-[#D9D9D9] relative">
                    <div class="w-[250px] bg-[#fff] absolute text-center left-[50%] translate-x-[-50%] top-[-10px]">Total Amount : {{ numberWithCommas(totalAmount) }} Point</div>
                </div>
                <button @click="clickTransferButton" v-if="isTransfer"
                        class="rounded mt-[50px] w-full text-center h-[62px] leading-[62px] bg-[#4640DE] text-white">
                    Transfer
                </button>
                <button @click="clickDeductButton" v-if="isDeduct"
                        class="rounded mt-[50px] w-full text-center h-[62px] leading-[62px] bg-[#4640DE] text-white">
                    Deduct
                </button>
            </div>
        </div>

        <!-- Popups -->
        <Teleport to="body">
            <designDialogueModal 
                v-model="showFailedListPopup"
                :modalTitle="'Invalid or unregistered email found\nin the template.'"
                :modalContent="'Please check the rows below and try again.'"
                :iconConfig="{label: 'warning', color : 'text-red-400', width: 24, height: 24, weight : 'FILL' }"
                :confirmConfig="{ label : 'Download & Check the error message', type : 'RED', action : 'confirm', variant : 'FILL' }"
                modalWidth="w-[400px]"
                @confirm="getErrorExcelFile"
            >
                <template v-slot:confirm-front-icon>
                    <DownloadIcon :color="'text-invert'" width="16" height="16" />
                </template>
                <template v-slot:modal-content-extra>
                    <div class="font-body-sm-semibold text-accent-red-3">
                        {{ getFailEmailList() }}
                    </div>
                </template>
            </designDialogueModal>
            <designDialogueModal 
                v-model="showEmptyFilePopup"
                :modalTitle="'No email found in the template.'"
                :modalContent="'Please add email addresses to the rows below and try again.'"
                :dialogType="'ALERT'"
                modalWidth="w-[400px]"
                @confirm="()=>{showEmptyFilePopup=false}"
            />
            <DesignConfirmPopup
                v-model="showAlertPopup"
                :showTitle="true"
                title="Failed"
                :confirmDescription="alertMessage"
                buttonText="OK"
                confirmBtnType="RED_SOLID"
                :showCancel="false"
            />
            <DesignConfirmPopup
                v-model="showConfirmPopup"
                :showTitle="true"
                title="Confirm"
                :confirmDescription="confirmMessage"
                buttonText="Confirm"
                :showCancel="true"
                cancelText="Cancel"
                :hasAfterConfirm="true"
                @after-confirm="clickSubmit"
            />
        </Teleport>
    </div>
</template>

<script>
import ElementsButton from '@/components/elements/ElementsButton.vue';
import ElementsInput from '@/components/elements/ElementsInput.vue';
import DesignConfirmPopup from '@/components/design-system/design-confirm-popup.vue';
import { FileValidateUtil } from '@/plugins/app-util.js';
import ChevronLeftIcon from '@/components/design-system/icon/chevron-left.vue';
import ChevronRightIcon from '@/components/design-system/icon/chevron-right.vue';
import ArrowDropDownIcon from '@/components/design-system/icon/arrow-drop-down.vue';
import designDialogueModal from '@/components/design-system/design-dialogue-modal.vue';
import DownloadIcon from '@/components/design-system/icon/download-icon.vue';

export default {
    components: {
        ElementsButton,
        ElementsInput,
        DesignConfirmPopup,
        ChevronLeftIcon,
        ChevronRightIcon,
        ArrowDropDownIcon,
        designDialogueModal, 
        DownloadIcon,
    },
    props: {
        transactionType: String,
        taxApplied: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['clickTransferButton', 'clickDeductButton'],
    data() {
        return {
            // Wallets for sub-admin
            wallets: [],
            walletIndex: 0,
            selectedWallet: {
                walletCategorySeq: null,
                walletCategoryName: null,
                subAdminAvailableMileage: 0,
            },
            showWalletDropdown: false,

            // File-upload specific
            uploadFile: null,
            inValidFileType: false,
            inValidFileTypeMessage: '',
            excelList: [],
            failedList: [],
            failedExcelPath : "",

            // Message/total
            transactionMessage: '',
            showTransactionMessageError: false,
            transactionMessageErrorMessage: 'Please provide a message to display.',
            totalAmount: 0,

            // Popups
            showFailedListPopup: false,
            showEmptyFilePopup: false,
            showAlertPopup: false,
            alertMessage: '',
            showConfirmPopup: false,
            confirmMessage: '',

            // Scroll lock
            scrollYBackup: 0,

            hasRunValidation: false,
        };
    },
    computed: {
        canPrev() {
            return (this.wallets?.length || 0) > 0 && this.walletIndex > 0;
        },
        canNext() {
            return (this.wallets?.length || 0) > 0 && this.walletIndex < (this.wallets.length - 1);
        },
        isAnyPopupOpen() {
            return this.showEmptyFilePopup || this.showAlertPopup || this.showConfirmPopup || this.showFailedListPopup;
        },
        isTransfer() { return this.transactionType === 'transfer'; },
        isDeduct() { return this.transactionType === 'deduct'; },
    },
    watch: {
        isAnyPopupOpen(val) {
            if (val) {
                this.lockBodyScroll();
            } else {
                this.unlockBodyScroll();
            }
        },
    },
    mounted() {
        this.getWallets().then(() => {
            if (!this.selectedWallet?.walletCategorySeq && this.wallets.length > 0) {
                this.selectWallet(this.wallets[0].walletCategorySeq);
            }
        });
    },
    methods: {
        // Wallet header behaviors
        toggleWalletDropdown() { this.showWalletDropdown = !this.showWalletDropdown; },
        prevWallet() {
            if (this.wallets.length === 0 || this.walletIndex <= 0) return;
            this.walletIndex = this.walletIndex - 1;
            this.selectWallet(this.wallets[this.walletIndex].walletCategorySeq);
        },
        nextWallet() {
            if (this.wallets.length === 0 || this.walletIndex >= this.wallets.length - 1) return;
            this.walletIndex = this.walletIndex + 1;
            this.selectWallet(this.wallets[this.walletIndex].walletCategorySeq);
        },
        selectWallet(seq) {
            const list = this.wallets || [];
            const idx = list.findIndex(o => o?.walletCategorySeq === seq);
            const found = idx >= 0 ? list[idx] : null;
            this.selectedWallet = {
                walletCategorySeq: found?.walletCategorySeq || null,
                walletCategoryName: found?.walletCategoryName || null,
                subAdminAvailableMileage: Number(found?.walletSubAdminMileage) || 0,
            };
            if (idx >= 0) this.walletIndex = idx;
            this.showWalletDropdown = false;
        },
        getWallets() {
            const url = this.$api('uri', 'get-sub-admin-wallets');
            return this.$axios.get(url).then(res => {
                this.wallets = res.data?.data?.list || [];
                if (this.wallets && this.wallets.length > 0) {
                    this.selectWallet(this.wallets[0].walletCategorySeq);
                }
            }).catch(err => console.error('get Wallets error', err));
        },

        // Common helpers
        lockBodyScroll() {
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden';
        },
        unlockBodyScroll() {
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';
            window.scrollTo(0, y);
        },
        convertCommaStringToNumber(value) {
            if (value == null || value === '') {
                return '';
            }
            return Number(String(value).replaceAll(',', ''));
        },
        convertNumberToCommaString(value) {
            let decimalValue = String(value);
            decimalValue = decimalValue.replace(/(\..*)\./g, '$1');
            const parts = decimalValue.split('.');
            parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            return parts.join('.');
        },
        numberWithCommas(number) {
            if (number == null || Number.isNaN(Number(number))) {
                return number;
            }
            return this.convertNumberToCommaString(number);
        },

        // File upload handlers
        clickUploadButton(event) {
            event.preventDefault();

            this.inValidFileType = false;
            this.inValidFileTypeMessage = '';

            if (this.uploadFile == null) {
                alert('Please upload a file.');
                return;
            }

            if (FileValidateUtil.isXlsFile(this.uploadFile)) {
                this.inValidFileType = true;
                this.inValidFileTypeMessage = FileValidateUtil.errMsg.xls;
                return;
            }
            if (!FileValidateUtil.isXlsxFile(this.uploadFile)) {
                this.inValidFileType = true;
                this.inValidFileTypeMessage = FileValidateUtil.errMsg.xlsxOnly;
                return;
            }

            const formProps = new FormData(this.$refs['fileUploadForm']);
            formProps.append('isTransfer', this.isTransfer);
            const url = this.$api('uri', 'post-transaction-execute-file');

            this.$axios
                .post(url, formProps, {
                    headers: { 'Content-Type': 'multipart/form-data;' },
                })
                .then((res) => {
                    this.failedList = res.data.data.failedList;
                    this.failedExcelPath = res.data.data.failedPath;
                    if (this.failedList.length > 0) {
                        this.showFailedListPopup = true;
                        return;
                    }

                    if(res.data.data.excelList.length == 0 ){
                        this.showEmptyFilePopup = true;
                        return;
                    }

                    this.excelList = res.data.data.excelList;
                    this.excelList?.forEach((employee) => {
                        if (employee.transactionNote != null) {
                            employee.transactionNote = employee.transactionNote.substring(0, 20);
                        }
                        employee.isNotInteger = false;
                        employee.emptyAmount = false;
                        const originMileage = employee.mileageVolume;
                        const mileageNum = Number(originMileage);
                        const isInvalidNum = !originMileage || Number.isNaN(mileageNum) || mileageNum==0;
                        if(isInvalidNum){
                            employee.emptyAmount = true;
                            employee.mileageVolume = null;
                            employee.mileageVolumeFormat = null;
                            return;
                        }
                        employee.isNotInteger = !Number.isInteger(mileageNum);
                        employee.mileageVolume = this.convertCommaStringToNumber(originMileage);
                        employee.mileageVolumeFormat = this.numberWithCommas(originMileage);
                    });
                    this.calTotalAmount();
                })
                .catch((err) => {
                    const errCode = err?.response?.data?.code;
                    if (errCode == FileValidateUtil.notSupported) {
                        this.inValidFileType = true;
                        this.inValidFileTypeMessage = FileValidateUtil.errMsg.xlsxOnly;
                        return;
                    }
                    if (errCode == FileValidateUtil.strictOOXML) {
                        this.inValidFileType = true;
                        this.inValidFileTypeMessage = FileValidateUtil.errMsg.strictXlsx;
                        return;
                    }

                    alert('Please upload the excel template file.');
                    console.error('err : ', err);
                });
        },
        removeFile() {
            this.uploadFile = null;
            this.inValidFileType = false;
            this.inValidFileTypeMessage = '';
        },
        getFailEmailList() {
            if (this.failedList == null || this.failedList.length == 0) return '';
            const emailList = [];
            for (let i = 0; i < this.failedList.length; ++i) {
                emailList.push(this.failedList[i].rowIndex);
            }
            return '*Rows: ' + emailList.join(', ');
        },
        getErrorExcelFile(){
            const url = this.$api('uri', 'get-invite-bulk-error');
            const json_query = JSON.stringify({ excelFilePath: this.failedExcelPath });
            const a = document.createElement('a');
            a.href =  url + `?${encodeURIComponent('json_query')}=${encodeURIComponent(json_query)}`;
            document.body.appendChild(a); 
            a.click();
            document.body.removeChild(a);
            this.showFailedListPopup = false;
        },

        // Row edits and total calc
        onInputMileage(event, employee) {
            const input = event.target.value;
            const mileage = input.replace(/[^0-9]/g, '');
            if (mileage==null || mileage=='' || mileage==0) {
                employee.emptyAmount = true;
            } else {
                employee.emptyAmount = false;
            }
            const result = this.convertCommaStringToNumber(mileage);
            employee.mileageVolume = result;
            employee.mileageVolumeFormat = this.numberWithCommas(result);
            this.calTotalAmount();
            employee.isNotInteger = !Number.isInteger(Number(result));
        },
        clickDeleteButton(index) {
            this.excelList.splice(index, 1);
            this.calTotalAmount();
        },
        calTotalAmount() {
            this.totalAmount = 0;
            if ((this.excelList || []).length == 0) return;
            this.excelList.forEach((employee) => {
                if (!employee.mileageVolume) return;
                const points = Number(employee.mileageVolume);
                this.totalAmount += points;
            });
        },

        // Validation and submit
        onMessageInput(val) {
            if (!this.hasRunValidation) return;
            this.showTransactionMessageError = !String(val).trim();
        },
        initValidationErrors() {
            this.showTransactionMessageError = false;
        },
        handleTypeChange() {
            // Reset all state on transfer/deduct switch
            this.initValidationErrors();
            this.uploadFile = null;
            this.excelList = [];
            this.failedList = [];
            this.failedExcelPath = '';
            this.transactionMessage = '';
            this.totalAmount = 0;
            this.showFailedListPopup = false;
            this.showAlertPopup = false;
            this.alertMessage = '';
            this.showConfirmPopup = false;
            this.confirmMessage = '';
            this.hasRunValidation = false;
            // refresh wallets
            this.getWallets();
        },
        checkValidation() {
            this.hasRunValidation = true;
            this.initValidationErrors();
            
            if ((this.transactionMessage || '').trim().length === 0) {
                this.showTransactionMessageError = true;
            }
            const hasWallet = !!(this.selectedWallet && this.selectedWallet.walletCategorySeq);
            if (this.isTransfer && (this.showTransactionMessageError || !hasWallet)) {
                this.openAlertPopup('Transfer failed due to an invalid input. Please check and try again.');
                return 1;
            }
            if (this.isDeduct && (this.showTransactionMessageError || !hasWallet)) {
                this.openAlertPopup('Deduct failed due to an invalid input. Please check and try again.');
                return 1;
            }

            // dedupe
            let validationCnt = 0;
            this.excelList = (this.excelList || []).filter((row, idx) => {
                if (row.email == null || row.email === '') return false;
                return this.excelList.findIndex((item) => item.email == row.email) === idx;
            });

            // numeric checks
            (this.excelList || []).forEach((employee) => {
                const points = Number(employee.mileageVolume);
                if (Number.isNaN(points) || points < 1 || !Number.isInteger(points)) {
                    validationCnt++;
                }
            });

            // 세금 적용 시, 각 직원별 전송 포인트가 최소 2 이상인지 확인
            if (this.isTransfer && this.taxApplied) {
                const hasBelowMinimum = (this.excelList || []).some((employee) => {
                    const amount = Number(employee.mileageVolume || 0);
                    return Number.isNaN(amount) || amount < 2;
                });

                if (hasBelowMinimum) {
                    this.openAlertPopup('Please enter at least 2 points for each employee.');
                    return 1;
                }
            }

            if (validationCnt > 0) {
                this.openAlertPopup('You need to enter the point amount.');
                return validationCnt;
            }
            if ((this.excelList || []).length == 0) {
                this.openAlertPopup('Please add employees before proceeding.');
                validationCnt++;
            }
            if (validationCnt > 0) return validationCnt;
            this.calTotalAmount();
            return validationCnt;
        },
        clickTransferButton() {
            if (this.checkValidation() != 0) {
                return;
            }
            // compare against selected wallet available points
            if (this.selectedWallet?.subAdminAvailableMileage != null && this.totalAmount > this.selectedWallet.subAdminAvailableMileage) {
                this.openAlertPopup('The amount of transfer is more than total');
                return;
            }
            this.openConfirmPopup('Are you sure you want to transfer points?');
        },
        clickDeductButton() {
            if (this.checkValidation() != 0) {
                return;
            }
            this.openConfirmPopup('Are you sure you want to deduct points?');
        },
        clickSubmit() {
            this.hideConfirmPopup();
            if (this.isTransfer) {
                const employeeTransactionForms = (this.excelList || []).map((emp) => ({
                    employeeSeq: emp.employeeSeq,
                    employeeName: emp.employeeName,
                    employeeEmail: emp.email,
                    mileageVolume: Number(emp.mileageVolume || 0),
                    transactionNote: (emp.transactionNote || '').trim(),
                }));
                const transferForm = {
                    walletCategorySeq: this.selectedWallet.walletCategorySeq,
                    walletCategoryName: this.selectedWallet.walletCategoryName,
                    totalPersonCount: (this.excelList || []).length,
                    totalMileageVolume: Number(this.totalAmount || 0),
                    employeeTransactionForms,
                    transactionNote: (this.transactionMessage || '').trim(),
                    taxAppliedYN: this.taxApplied? 'Y' : 'N',
                };
                this.$emit('clickTransferButton', transferForm);
            } else if (this.isDeduct) {
                const employeeTransactionForms = (this.excelList || []).map((emp) => ({
                    employeeSeq: emp.employeeSeq,
                    employeeName: emp.employeeName,
                    employeeEmail: emp.email,
                    mileageVolume: Number(emp.mileageVolume || 0),
                    transactionNote: (emp.transactionNote || '').trim(),
                }));
                const deductForm = {
                    walletCategorySeq: this.selectedWallet.walletCategorySeq,
                    walletCategoryName: this.selectedWallet.walletCategoryName,
                    totalPersonCount: (this.excelList || []).length,
                    totalMileageVolume: Number(this.totalAmount || 0),
                    employeeTransactionForms,
                    transactionNote: (this.transactionMessage || '').trim(),
                };
                this.$emit('clickDeductButton', deductForm);
            }
        },

        // popups
        openAlertPopup(message) {
            this.alertMessage = message;
            this.showAlertPopup = true;
        },
        hideAlertPopup() {
            this.showAlertPopup = false;
        },
        openConfirmPopup(message) {
            this.confirmMessage = message;
            this.showConfirmPopup = true;
        },
        hideConfirmPopup() {
            this.showConfirmPopup = false;
        },
        resetState() {
            // clear validation/errors
            this.initValidationErrors();
            this.hasRunValidation = false;
            // clear file mode data
            this.uploadFile = null;
            this.excelList = [];
            this.failedList = [];
            this.failedExcelPath = '';
            this.transactionMessage = '';
            this.totalAmount = 0;
            this.showFailedListPopup = false;
            this.showAlertPopup = false;
            this.alertMessage = '';
            this.showConfirmPopup = false;
            this.confirmMessage = '';
            // refresh wallets
            this.getWallets();
        },
    },
};
</script>


